import { v5 as uuidv5 } from "uuid";

import type {
  CollectionListSmartCardResponseSchema,
  CollectionListSmartCardValueSchema,
  CollectionSmartCardItemSchema,
  EventSmartEntitySmartCardItemSchema,
  MarkdownSmartCardResponseSchema,
  MarkdownSmartCardValueSchema,
  MentionedBySmartCardResponseSchema,
  NoteListSmartCardResponseSchema,
  NoteListSmartCardValueSchema,
  NoteSmartCardItemSchema,
  SmartContextPayloadValueSchema,
  SmartViewKind,
  SmartViewSchema,
  TimelineSmartCardResponseSchema,
  TimelineSmartCardValueSchema,
  UnknownSmartCardResponseSchema,
} from "@/domains/openapi/types";

export type SmartView = SmartViewSchema;

export type SmartCard =
  | NoteListSmartCardResponseSchema
  | MentionedBySmartCardResponseSchema
  | CollectionListSmartCardResponseSchema
  | TimelineSmartCardResponseSchema
  | MarkdownSmartCardResponseSchema
  | UnknownSmartCardResponseSchema;

export type NoteSmartCardItem = NoteSmartCardItemSchema;
export type CollectionSmartCardItem = CollectionSmartCardItemSchema;
export type EventSmartEntityItem = EventSmartEntitySmartCardItemSchema;

export type NoteListSmartCardValue = NoteListSmartCardValueSchema;
export type CollectionListSmartCardValue = CollectionListSmartCardValueSchema;
export type TimelineSmartCardValue = TimelineSmartCardValueSchema;
export type MarkdownSmartCardValue = MarkdownSmartCardValueSchema;

export type NoteListSmartCard = NoteListSmartCardResponseSchema;
export type MentionedBySmartCard = MentionedBySmartCardResponseSchema;
export type CollectionListSmartCard = CollectionListSmartCardResponseSchema;
export type EventTimelineSmartCard = TimelineSmartCardResponseSchema;
export type MarkdownSmartCard = MarkdownSmartCardResponseSchema;
export type UnknownSmartCard = UnknownSmartCardResponseSchema;

export const DEFAULT_SMART_VIEW_KIND: SmartViewKind = "HEADS_UP";
export const SMART_VIEW_KIND_OPTIONS: SmartViewKind[] = ["HEADS_UP", "PULSE"];

const SMART_CONTEXT_NAMESPACE = "0CCCE77F-6835-4CF9-A263-57DAFBAAA218";

export interface ComputeSmartContextIdOptions {
  spaceAccountId: SmartContextPayloadValueSchema["space_account_id"];
  activeNoteId?: SmartContextPayloadValueSchema["active_note_id"];
  activeCollectionId?: SmartContextPayloadValueSchema["active_collection_id"];
  query?: SmartContextPayloadValueSchema["query"];
  continuationId?: SmartContextPayloadValueSchema["continuation_id"];
  smartViewKind?: SmartViewKind | null;
}

export const computeSmartContextId = ({
  spaceAccountId,
  activeNoteId,
  activeCollectionId,
  query,
  continuationId,
  smartViewKind = DEFAULT_SMART_VIEW_KIND,
}: ComputeSmartContextIdOptions): string => {
  const segments: string[] = [];

  if (activeNoteId) {
    segments.push(`ACTIVE_NOTE::${activeNoteId}`);
  } else if (activeCollectionId) {
    segments.push(`ACTIVE_COLLECTION::${activeCollectionId}`);
  } else if (query) {
    segments.push(`QUERY::${query}`);
  } else {
    segments.push("NONE");
  }

  if (activeNoteId && query) {
    segments.push(`QUERY::${query}`);
  }

  if (activeCollectionId && query) {
    segments.push(`COLLECTION_QUERY::${activeCollectionId}::${query}`);
  }

  if (smartViewKind) {
    segments.push(`VIEW_KIND::${smartViewKind}`);
  }

  if (continuationId) {
    segments.push(`CONTINUATION::${continuationId}`);
  }

  const namespaceName = segments.map(segment => segment.toUpperCase()).join("::");
  const namespace = uuidv5(namespaceName, SMART_CONTEXT_NAMESPACE);
  const upperSpaceAccountId = spaceAccountId.toUpperCase();

  return uuidv5(upperSpaceAccountId, namespace);
};

export const isNoteListSmartCard = (card: SmartCard): card is NoteListSmartCard =>
  card.kind === "NOTE_LIST";

export const isMentionedBySmartCard = (card: SmartCard): card is MentionedBySmartCard =>
  card.kind === "MENTIONED_BY_NOTES_LIST";

export const isCollectionListSmartCard = (card: SmartCard): card is CollectionListSmartCard =>
  card.kind === "COLLECTION_LIST";

export const isEventTimelineSmartCard = (card: SmartCard): card is EventTimelineSmartCard =>
  card.kind === "EVENT_TIMELINE";

export const isMarkdownSmartCard = (card: SmartCard): card is MarkdownSmartCard =>
  card.kind === "MARKDOWN";

export const getSmartViewKind = (smartView: SmartView): SmartViewKind => {
  const rawKind = (smartView.value_json as { kind?: SmartViewKind })?.kind;
  if (rawKind === "PULSE") {
    return "PULSE";
  }
  if (rawKind === "UNKNOWN") {
    return "UNKNOWN";
  }
  return "HEADS_UP";
};
